import { CodeBlock, monokai } from "react-code-blocks";
import { useOutletContext } from "react-router-dom";
import { TabNames } from "../Assets/variables";
import { useEffect } from "react";

const APIDefinitionFixedIncomeReferenceDataExtended = () => {
    const {setActiveTabItem} = useOutletContext();

    useEffect(() => {
        setActiveTabItem(TabNames.APIDefinition);
    }, []);

    return (
        <div id="tab-definition" class="api-panel">
            <div className="api-definition-container">
                <p className="bulk-paragraph">The Bulk API can be used for retrieving Extended Fixed Income Reference Data, either with a full extract of reference data in minutes via the snapshot end point, or through regular calls to the /update end point, to retrieve the latest Fixed Income Reference Data updates.</p>
                <span className="bulk-heading-2 heading-2">Bulk API End Points</span>
                <table>
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th>URL</th>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Snapshot End Point</td>
                            <td><code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot</code></td>
                            <td>Use this endpoint to download a full weekly extract of Extended Fixed Income Reference Data.</td>
                        </tr>
                        <tr>
                            <td>Update End Point</td>
                            <td><code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/update</code></td>
                            <td>Use this endpoint to download Extended Fixed Income Reference deltas.</td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-2">Extended Fixed Income Reference Data Datasets</span>
                <table>
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th>URL</th>
                            <th>Comments</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Extended Corporate And Government Bonds Details</td>
                            <td><code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot/../CG</code></td>
                            <td>Only available for permissioned users.</td>
                        </tr>
                        <tr>
                            <td>Extended Structured Finance Bond Details</td>
                            <td><code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot/../SF</code></td>
                            <td>Only available for permissioned users.</td>
                        </tr>
                        <tr>
                            <td>Extended US Municipal Bond Details</td>
                            <td><code>https://api.six-group.com/bulk/v1/referenceData/extendedFixedIncome/snapshot/../USMunis</code></td>
                            <td>Only available for permissioned users.</td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-2">Bulk API Optional URL Parameters</span>
                <p>The Bulk API <code>/update</code> URL can be adjusted either forwards or backwards by providing the <code>from</code> and <code>to</code> parameters, using ISO time format. For example:</p>
                <div className="code-example">
                    <CodeBlock
                        text={"…/update/…?from=2024-09-12T12:59:34.388Z&to=2024-09-12T15:59:34.388Z"}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>
                <p>This will retrieve the updates between <code>2024-09-12T12:59:34.388Z</code> and <code>2024-09-12T15:59:34.388Z</code>. Ideally, when using the time parameters, URL format should be used.</p>

                <table>
                    <thead>
                        <tr>
                            <th>URL Parameter</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>from</td>
                            <td>
                                <p>Should typically be provided equal either to the timestamp <code>lastUpdated</code> of the latest snapshot which you downloaded or the time of your last query.</p>
                                <p>However, <code>from</code> can also be set to any prior time, as it simply provides the lower bound timestamp for the data presented.</p>
                            </td>
                        </tr>
                        <tr>
                            <td>to</td>
                            <td>
                                <p>Should typically be provided equal either to now or to some time since the last update, in order to throttle the amount of updates to download.</p>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p>Note that the time difference between <code>from</code> and <code>to</code> should not exceed 7 days, otherwise an error will be thrown.</p>

                <span className="heading-2">Bulk API Data Life Cycle</span>

                <table>
                    <thead>
                        <tr>
                            <th>Event Description</th>
                            <th>Frequency</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Snapshot Creation Time</td>
                            <td>Every Friday evening or Saturday</td>
                        </tr>
                        <tr>
                            <td>Data Refresh Frequency</td>
                            <td>3 times a day Monday to Friday</td>
                        </tr>
                        <tr>
                            <td>Data Availability</td>
                            <td>Direct access to all data through Bulk API is removed 15 days after the data was first made available. Following the removal, On Demand Access is possible through support for 2,5 months after this 15-day period. In total, data should be accessible either directly or On Demand for 3 months after it was first made available via the Bulk API.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default APIDefinitionFixedIncomeReferenceDataExtended;
